/**
 * Structure edits the palette, canvas and inspector perform on a graph.
 *
 * Pure functions over `{nodes, edges}` rather than methods on the page, so each one is
 * testable and none of them can accidentally close over stale React state.
 */

/** Node ids must match services/workflows/graph.js's /^[A-Za-z0-9_-]{1,64}$/ and be unique
 *  within the graph. */
export function nextNodeId(type, nodes) {
  let n = 1;
  while (nodes.some((node) => node.id === `${type}_${n}`)) n += 1;
  return `${type}_${n}`;
}

/** Somewhere free-ish, for a click-to-add from the palette (a drop supplies its own point). */
function scatterPosition(nodes) {
  return { x: 200 + (nodes.length % 4) * 60, y: 40 + nodes.length * 40 };
}

export function addNode(graph, { nodeType, data = {}, position }) {
  const id = nextNodeId(nodeType, graph.nodes);
  const node = {
    id,
    type: nodeType,
    position: position || scatterPosition(graph.nodes),
    data: { label: data.label || id, ...data },
  };
  return { graph: { ...graph, nodes: [...graph.nodes, node] }, id };
}

/** A copy keeps the config but never the wiring: cloning the edges would give the source
 *  node two outgoing edges on one branch, which services/workflows/graph.js rejects. */
export function duplicateNode(graph, nodeId) {
  const source = graph.nodes.find((n) => n.id === nodeId);
  if (!source) return { graph, id: null };
  const id = nextNodeId(source.type, graph.nodes);
  const node = {
    ...source,
    id,
    position: { x: (source.position?.x || 0) + 40, y: (source.position?.y || 0) + 60 },
    data: { ...source.data, label: `${source.data?.label || source.type} copy` },
    selected: false,
  };
  return { graph: { ...graph, nodes: [...graph.nodes, node] }, id };
}

/** Deleting a node also drops every edge touching it — validateGraph rejects a dangling
 *  edge, so leaving one behind would make the workflow unsaveable with no visible cause. */
export function deleteNode(graph, nodeId) {
  return {
    nodes: graph.nodes.filter((n) => n.id !== nodeId),
    edges: graph.edges.filter((e) => e.source !== nodeId && e.target !== nodeId),
  };
}

export function updateNodeData(graph, nodeId, data) {
  return { ...graph, nodes: graph.nodes.map((n) => (n.id === nodeId ? { ...n, data } : n)) };
}

/**
 * Which nodes a validation error is talking about, best-effort.
 *
 * validateGraph returns ONE human-readable sentence, not a per-node report — but it names the
 * offender ('Node "llm_1" is unreachable from the entry node'), so a whole-word id match
 * points the user at the right card instead of making them re-read the graph. Whole-word so
 * `llm_1` does not also light up `llm_10`.
 */
export function nodesNamedInError(nodes, message) {
  if (!message) return new Set();
  return new Set(nodes
    .map((n) => n.id)
    .filter((id) => new RegExp(`\\b${id.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`).test(message)));
}

/**
 * Node ids reachable BACKWARDS from `nodeId` — the only ones whose output exists by the time
 * this node runs, and therefore the only valid `{{id.output}}` references to offer.
 */
export function upstreamNodeIds(graph, nodeId) {
  const incoming = new Map();
  for (const edge of graph.edges) {
    if (!incoming.has(edge.target)) incoming.set(edge.target, []);
    incoming.get(edge.target).push(edge.source);
  }
  const seen = new Set();
  const queue = [...(incoming.get(nodeId) || [])];
  while (queue.length) {
    const id = queue.pop();
    if (seen.has(id)) continue; // also terminates on a cycle the canvas is holding mid-edit
    seen.add(id);
    queue.push(...(incoming.get(id) || []));
  }
  return [...seen];
}
